import RichTextBlockEleve from '../VueQuestion/RichTextBlockEleve';
import AutoFitText from './AutoFitText';
import ChoicesGrid from './ChoicesGrid';
import TanoProjo from './TanoProjo';
import { QCM_NAVY_BG, FONT_TITRE } from '../../utils/qcmTheme';
import '../../styles/projectionFonts.css';
import { sansRappelBrouillon } from '../../utils/rappelBrouillon';

/**
 * Carte de revue projetée en fin de séance : l'énoncé rejoué en grand, la
 * bonne réponse mise en vedette dans la grille des choix, et le pourcentage de
 * la classe qui l'avait trouvée (D5). Pas de noms d'élèves — le classement,
 * c'est le podium, et il est déjà passé.
 *
 * L'écran est PASSIF comme tout le reste de la projection : la navigation
 * question par question vient du téléphone, par le flux.
 */
const ProjectionRevue = ({ titre, carte, totalQuestions, attenteRaison = null }) => {
  if (!carte) {
    // Sans carte on attend ; si on SAIT pourquoi (les résultats sont là mais
    // pas cette question), on le dit plutôt que de promettre une correction
    // qui ne viendra pas.
    return (
      <div className="h-screen w-full flex items-center justify-center p-8 overflow-hidden text-white" style={{ background: QCM_NAVY_BG }}>
        <div className="text-center space-y-6">
          <div className="mx-auto w-fit">
            <TanoProjo variant="idle" mood="idle" size={130} />
          </div>
          <p className="text-3xl font-black" style={{ fontFamily: FONT_TITRE }}>On corrige ensemble…</p>
          <p className="font-semibold text-white/50">{attenteRaison || "La correction s'affichera automatiquement"}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-full flex flex-col overflow-hidden text-white"
      style={{ background: QCM_NAVY_BG, fontFamily: "'Inter', system-ui, sans-serif" }}
    >
      <div className="flex items-center justify-between px-9 pt-5 flex-shrink-0">
        <span
          className="font-bold text-xs uppercase text-white/60 overflow-hidden text-ellipsis whitespace-nowrap"
          style={{ letterSpacing: '.16em', fontFamily: FONT_TITRE }}
        >
          TanoEd Live{titre ? ` · ${titre}` : ''} · Correction
        </span>
        <span className="font-black text-xl tabular-nums text-white flex-shrink-0" style={{ fontFamily: FONT_TITRE }}>
          Question {carte.questionOrdre}{totalQuestions > 0 ? `/${totalQuestions}` : ''}
        </span>
      </div>

      {/* Mêmes réglages que l'énoncé du quizz (ProjectionQCM, 2026-09-20) :
          graisse 700 et tracking neutre, sinon l'espace fine des milliers
          disparaît sous les traits d'Archivo en 900. C'est le même énoncé, sur
          le même vidéoprojecteur : les deux écrans ne peuvent pas diverger. */}
      <AutoFitText
        key={carte.questionOrdre}
        fitKey={carte.questionOrdre}
        minSize={16}
        maxSize={72}
        className="proj-fade-up flex-1 px-9 pt-3 font-bold"
      >
        <div
          className="leading-[1.18] text-white [&_img]:max-h-[38vh] [&_img]:object-contain [&_svg]:max-h-[38vh] [&_svg]:max-w-full"
          style={{ fontFamily: FONT_TITRE, letterSpacing: '0' }}
        >
          <RichTextBlockEleve texte={sansRappelBrouillon(carte.texte)} elements={carte.elements} theme="dark" />
        </div>
      </AutoFitText>

      <ChoicesGrid key={`revue-${carte.questionOrdre}`} choix={carte.choix} bonneLettre={carte.bonneLettre} />

      <div className="mt-auto flex-shrink-0 px-9 pt-4 pb-6 bg-white/[0.06] text-white" style={{ borderTop: '1px solid rgba(255,255,255,.1)' }}>
        <div className="flex items-center gap-6 flex-wrap">
          <p
            className="font-black tabular-nums"
            style={{ color: carte.couleur, fontSize: 'clamp(40px, 6vw, 72px)', fontFamily: FONT_TITRE, lineHeight: 1 }}
          >
            {carte.pctCorrect}%
          </p>
          <div>
            <p className="text-sm font-semibold text-white/50">
              de bonnes réponses{carte.nbReponses > 0 ? ` · ${carte.nbBonnes}/${carte.nbReponses}` : ''}
            </p>
            {carte.bonneReponse ? (
              <p className="font-black text-white" style={{ fontSize: 'clamp(18px, 2.4vw, 28px)', fontFamily: FONT_TITRE }}>
                Réponse : <span className="text-emerald-400">{carte.bonneReponse}</span>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectionRevue;
